import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowRight, ArrowUpRight, CheckCircle2, Footprints, Users, MapPin, Building2, Bus, Route,
  MessageCircle, Mail, Globe, X, BarChart3, FlaskConical, Radar,
} from "lucide-react";
import {
  EVENT, IMG, QUESTIONS, FEATURES, INDUSTRIES, SIGNALS, PROOF, TESTIMONIAL, DEMO,
} from "./content";
import CandidateViz from "./CandidateViz";

// landhep.id/flei — the FLEI 2026 exhibitor offer.
//
// Content comes from the team's static page (flei-landing-page-main); the design is
// redone in the landhep.id landing tone — brand greens, Plus Jakarta Sans, the same
// section rhythm and card shapes — so the event page reads as part of the product
// rather than a separate microsite.
//
// The original form called preventDefault() and did nothing, so every submission was
// lost. Here it is written to the team's Google Sheet through an Apps Script web app
// (apps-script.gs). If that endpoint is not set or the request fails, the visitor is
// sent to WhatsApp with the answers filled in instead, so no lead is dropped.

const SIGNAL_ICONS = [Route, Footprints, Users, MapPin, Building2, Bus];
const PROOF_ICONS = [Radar, BarChart3, FlaskConical];

function Eyebrow({ children, light = false }) {
  return (
    <span className={`text-xs font-semibold uppercase tracking-[0.2em] ${light ? "text-brand-300" : "text-brand-700"}`}>
      {children}
    </span>
  );
}

function Screen({ src, alt }) {
  return (
    <figure className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
      <div className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-3 py-2">
        <span className="h-2 w-2 rounded-full bg-slate-300" />
        <span className="h-2 w-2 rounded-full bg-slate-300" />
        <span className="h-2 w-2 rounded-full bg-slate-300" />
      </div>
      <img src={src} alt={alt} loading="lazy" className="block w-full" />
    </figure>
  );
}

// ── Mini neighbourhood map for the demo tabs ──────────────────────────────────
// Hand-placed, not generated: places cluster the way a neighbourhood does — food
// along the main road, offices in one block, homes in an estate — so the picture
// reads as a place rather than a pattern.
const POI_KINDS = [
  { key: "comp", label: "Kompetitor", color: "#ef4444" },
  { key: "food", label: "Kuliner", color: "#d97706" },
  { key: "office", label: "Kantor", color: "#2563eb" },
  { key: "home", label: "Hunian", color: "#7c3aed" },
];
const POIS = [
  // food strip along the main road
  { k: "food", x: 136, y: 88 }, { k: "food", x: 152, y: 80 }, { k: "food", x: 171, y: 76 }, { k: "food", x: 228, y: 63 },
  { k: "food", x: 244, y: 58 }, { k: "food", x: 262, y: 55 }, { k: "food", x: 186, y: 86 },
  // office block, north-east
  { k: "office", x: 276, y: 22 }, { k: "office", x: 290, y: 30 }, { k: "office", x: 302, y: 20 },
  { k: "office", x: 286, y: 42 }, { k: "office", x: 310, y: 38 },
  // housing estate, south-west
  { k: "home", x: 98, y: 122 }, { k: "home", x: 112, y: 130 }, { k: "home", x: 126, y: 122 }, { k: "home", x: 104, y: 140 },
  { k: "home", x: 132, y: 138 }, { k: "home", x: 146, y: 128 }, { k: "home", x: 120, y: 148 },
  // rivals — numbered like the list in the competitor tab
  { k: "comp", x: 212, y: 70, n: 1 }, { k: "comp", x: 182, y: 62, n: 2 }, { k: "comp", x: 246, y: 100, n: 3 },
];
const KIND = Object.fromEntries(POI_KINDS.map((k) => [k.key, k]));

function MiniMap({ mode }) {
  const cx = 200, cy = 80;
  const shown = mode === "comp" ? POIS.filter((p) => p.k === "comp") : POIS;
  return (
    <svg viewBox="0 0 400 160" className="block aspect-[5/2] w-full rounded-xl bg-[#eef3f0]" role="img"
      aria-label={mode === "comp" ? "Peta kompetitor di sekitar titik" : "Peta tempat di sekitar titik, per kategori"}>
      {/* blocks */}
      <rect x="268" y="10" width="62" height="42" rx="4" fill="#e2e8e4" />
      <rect x="84" y="110" width="78" height="46" rx="4" fill="#e2e8e4" />
      {/* roads: a main road, a cross street, a lane */}
      <path d="M-10 110 C 110 92, 240 60, 410 34" stroke="#fff" strokeWidth="11" fill="none" />
      <path d="M-10 110 C 110 92, 240 60, 410 34" stroke="#dbe3de" strokeWidth="1" fill="none" transform="translate(0 6)" />
      <path d="M216 -10 L 208 170" stroke="#fff" strokeWidth="7" fill="none" />
      <path d="M60 -10 C 110 30, 150 60, 158 170" stroke="#fff" strokeWidth="4" fill="none" />
      {/* the point and its radius */}
      <circle cx={cx} cy={cy} r="58" fill="rgba(29,145,96,0.07)" stroke="#1d9160" strokeWidth="1.2" strokeDasharray="4 4" />
      {shown.map((p, i) => p.n ? (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="7" fill={KIND[p.k].color} stroke="#fff" strokeWidth="1.5" />
          <text x={p.x} y={p.y + 3} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">{p.n}</text>
        </g>
      ) : (
        <circle key={i} cx={p.x} cy={p.y} r="4" fill={KIND[p.k].color} stroke="#fff" strokeWidth="1.2" />
      ))}
      <circle cx={cx} cy={cy} r="6" fill="#14543e" stroke="#fff" strokeWidth="2.5" />
    </svg>
  );
}

// ── Hero demo card: four tabs of illustrative figures ─────────────────────────
const TABS = ["Skor Lokasi", "Profil Demografi", "Analisa Kompetitor", "Aktivitas Sekitar"];

function DemoCard() {
  const [tab, setTab] = useState(0);
  return (
    <div className="rounded-2xl border border-white/10 bg-white p-5 text-[#171C1F] shadow-2xl">
      <div role="tablist" className="grid grid-cols-2 gap-1.5 rounded-xl bg-brand-50 p-1.5 sm:grid-cols-4">
        {TABS.map((t, i) => (
          <button key={t} role="tab" aria-selected={tab === i} onClick={() => setTab(i)}
            className={`rounded-lg px-2 py-2 text-[11px] font-semibold transition-colors ${tab === i ? "bg-brand-700 text-white shadow" : "text-slate-600 hover:bg-white"}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="mt-5 min-h-[210px]">
        {tab === 0 && (
          <div className="grid gap-4 sm:grid-cols-[auto_1fr] sm:items-center">
            <div className="flex h-32 w-32 flex-col items-center justify-center rounded-full border-[10px] border-brand-500 text-center">
              <span className="text-3xl font-extrabold tabular-nums">{DEMO.score.value}</span>
              <span className="text-[10px] text-slate-500">/100</span>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-bold">{DEMO.score.area}</p>
              {DEMO.score.others.map(([n, v]) => (
                <div key={n} className="flex items-center gap-3 text-xs">
                  <span className="w-28 shrink-0 text-slate-600">{n}</span>
                  <span className="h-1.5 flex-1 rounded-full bg-slate-100"><span className="block h-full rounded-full bg-brand-500" style={{ width: `${v}%` }} /></span>
                  <span className="w-6 text-right font-bold tabular-nums text-brand-700">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-brand-50 p-3"><p className="text-lg font-extrabold tabular-nums">{DEMO.demo.total}</p><p className="text-[10px] text-slate-500">Populasi</p></div>
              <div className="rounded-xl bg-brand-50 p-3"><p className="text-lg font-extrabold tabular-nums">{DEMO.demo.male}%</p><p className="text-[10px] text-slate-500">Laki-laki</p></div>
              <div className="rounded-xl bg-brand-50 p-3"><p className="text-lg font-extrabold tabular-nums">{DEMO.demo.female}%</p><p className="text-[10px] text-slate-500">Perempuan</p></div>
            </div>
            {DEMO.demo.ages.map(([a, v]) => (
              <div key={a} className="flex items-center gap-3 text-xs">
                <span className="w-12 shrink-0 text-slate-600">{a}</span>
                <span className="h-2 flex-1 rounded-full bg-slate-100"><span className="block h-full rounded-full bg-brand-500" style={{ width: `${v}%` }} /></span>
                <span className="w-9 text-right font-bold tabular-nums">{v}%</span>
              </div>
            ))}
          </div>
        )}
        {tab === 2 && (
          <div className="space-y-3">
            <MiniMap mode="comp" />
            {DEMO.comps.map(([n, d, t], i) => (
              <div key={n} className="flex items-center gap-3 rounded-lg border border-slate-100 px-3 py-2 text-xs">
                <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">{i + 1}</span>
                <span className="flex-1 font-semibold">{n}</span>
                <span className="text-slate-500">{d}</span>
                <span className={`font-bold ${t === "Tinggi" ? "text-red-600" : "text-amber-600"}`}>{t}</span>
              </div>
            ))}
          </div>
        )}
        {tab === 3 && (
          <div className="space-y-3">
            <MiniMap mode="poi" />
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-slate-600">
              {POI_KINDS.map(({ key, label, color }) => (
                <span key={key} className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full" style={{ background: color }} />
                  {label} <b className="tabular-nums text-slate-800">{POIS.filter((p) => p.k === key).length}</b>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <p className="mt-3 text-[10px] text-slate-400">Data ilustrasi</p>
    </div>
  );
}

// ── Industry chips: one note at a time, cycling on its own until touched ───────
function Industries() {
  const [i, setI] = useState(7);   // F&B first — FLEI is mostly food franchises
  const pausedUntil = useRef(0);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = setInterval(() => {
      if (Date.now() < pausedUntil.current) return;
      setI((n) => (n + 1) % INDUSTRIES.length);
    }, 2200);
    return () => clearInterval(t);
  }, []);
  const pick = (n) => { setI(n); pausedUntil.current = Date.now() + 6000; };
  return (
    <div onMouseEnter={() => { pausedUntil.current = Date.now() + 60_000; }} onMouseLeave={() => { pausedUntil.current = 0; }}>
      <div className="flex flex-wrap gap-2">
        {INDUSTRIES.map(([name], n) => (
          <button key={name} onClick={() => pick(n)}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${n === i ? "bg-brand-700 text-white" : "border border-brand-200 bg-white text-[#3E4947] hover:border-brand-400"}`}>
            {name}
          </button>
        ))}
      </div>
      <p aria-live="polite" className="mt-6 min-h-[3rem] max-w-2xl rounded-xl bg-white px-5 py-3 text-[15px] leading-relaxed text-[#3E4947] shadow-sm">
        <span className="font-semibold text-brand-700">{INDUSTRIES[i][0]} · </span>{INDUSTRIES[i][1]}
      </p>
    </div>
  );
}

// ── Trial form: sends the answers to the event WhatsApp line ──────────────────
function TrialDialog({ open, onClose }) {
  const [form, setForm] = useState({ bisnis: "", kategori: "", pic: "", wa: "", setuju: false, website: "" });
  const [status, setStatus] = useState("idle");   // idle | sending | saved | whatsapp
  const [sentUrl, setSentUrl] = useState(null);
  const firstField = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    firstField.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const waUrl = () => {
    const text = [
      `Halo, saya exhibitor di ${EVENT.name} dan ingin klaim free trial analisis Landhep.`,
      "",
      `Nama bisnis: ${form.bisnis.trim()}`,
      `Kategori bisnis: ${form.kategori.trim()}`,
      `Nama PIC: ${form.pic.trim()}`,
      `WhatsApp PIC: ${form.wa.trim()}`,
      "",
      "Saya menyetujui logo, nama bisnis, dan testimoni dapat digunakan untuk keperluan publikasi.",
    ].join("\n");
    return `https://wa.me/${EVENT.waNumber}?text=${encodeURIComponent(text)}`;
  };
  const toWhatsApp = () => {
    const url = waUrl();
    setSentUrl(url); setStatus("whatsapp");
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    if (!EVENT.sheetEndpoint) { toWhatsApp(); return; }
    setStatus("sending");
    try {
      // text/plain keeps this a "simple" request: Apps Script cannot answer a CORS
      // preflight, but it does return a readable response to a simple POST.
      // A request Google never answers must not leave the visitor on "Mengirim…".
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 12000);
      const res = await fetch(EVENT.sheetEndpoint, {
        signal: ctrl.signal,
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify({ ...form, page: window.location.href }),
      });
      clearTimeout(timer);
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setSentUrl(waUrl()); setStatus("saved");
    } catch {
      toWhatsApp();   // the sheet is unreachable — WhatsApp still gets the lead to the team
    }
  };
  const input = "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-100";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-brand-900/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="trial-title" onClick={(e) => e.stopPropagation()}
        className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-7 shadow-2xl md:p-8">
        <button onClick={onClose} aria-label="Tutup" className="absolute right-4 top-4 rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
          <X className="h-5 w-5" />
        </button>
        {status === "saved" ? (
          <div className="py-6 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-brand-500" />
            <h2 id="trial-title" className="mt-4 text-2xl font-bold">Terima kasih, data Anda sudah kami terima</h2>
            <p className="mt-3 leading-relaxed text-[#3E4947]">
              Tim Landhep akan menghubungi <b>{form.pic.trim()}</b> lewat WhatsApp di <b>{form.wa.trim()}</b> untuk menjadwalkan analisisnya.
            </p>
            <a href={sentUrl} target="_blank" rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand-200 px-6 py-3 text-sm font-semibold text-brand-700 hover:bg-brand-50">
              <MessageCircle className="h-4 w-4" /> Ingin lebih cepat? Chat kami sekarang
            </a>
          </div>
        ) : status === "whatsapp" ? (
          <div className="py-6 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-brand-500" />
            <h2 id="trial-title" className="mt-4 text-2xl font-bold">Tinggal kirim pesannya</h2>
            <p className="mt-3 leading-relaxed text-[#3E4947]">
              WhatsApp sudah terbuka dengan data Anda. Tekan <b>Kirim</b> di sana, lalu tim kami akan menghubungi Anda untuk menjadwalkan analisisnya.
            </p>
            <a href={sentUrl} target="_blank" rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-700 px-6 py-3 font-semibold text-white hover:bg-brand-800">
              <MessageCircle className="h-4 w-4" /> WhatsApp tidak terbuka? Buka di sini
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <h2 id="trial-title" className="text-2xl font-bold">Klaim Free Trial Landhep</h2>
              <p className="mt-2 text-sm leading-relaxed text-[#3E4947]">Khusus exhibitor {EVENT.name}, dapatkan free trial analisis Landhep untuk satu titik lokasi.</p>
            </div>
            <label className="block space-y-1.5"><span className="text-sm font-semibold">Nama Bisnis</span>
              <input ref={firstField} required value={form.bisnis} onChange={set("bisnis")} autoComplete="organization" className={input} /></label>
            <label className="block space-y-1.5"><span className="text-sm font-semibold">Kategori Bisnis</span>
              <input required list="flei-kategori" value={form.kategori} onChange={set("kategori")} placeholder="misalnya F&B, Retail" className={input} />
              <datalist id="flei-kategori">{INDUSTRIES.map(([n]) => <option key={n} value={n} />)}</datalist></label>
            <label className="block space-y-1.5"><span className="text-sm font-semibold">Nama PIC</span>
              <input required value={form.pic} onChange={set("pic")} autoComplete="name" className={input} /></label>
            <label className="block space-y-1.5"><span className="text-sm font-semibold">Nomor WhatsApp PIC</span>
              <input required type="tel" inputMode="tel" pattern={String.raw`[0-9 +\-\(\)]{8,}`} value={form.wa} onChange={set("wa")} autoComplete="tel" placeholder="08xx atau +62" className={input} /></label>
            {/* Honeypot — hidden from people, filled by bots; the sheet script drops those rows. */}
            <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
              value={form.website} onChange={set("website")} className="absolute left-[-9999px] h-0 w-0 opacity-0" />
            <label className="flex items-start gap-3 rounded-xl bg-brand-50 p-4 text-xs leading-relaxed text-[#3E4947]">
              <input required type="checkbox" checked={form.setuju} onChange={set("setuju")} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700" />
              <span>Dengan mengisi formulir ini, Anda menyetujui bahwa logo, nama bisnis, dan testimoni yang diberikan dapat digunakan dan ditampilkan untuk keperluan publikasi.</span>
            </label>
            <button type="submit" disabled={status === "sending"}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-700 py-3.5 font-semibold text-white transition-colors hover:bg-brand-800 disabled:opacity-60">
              {status === "sending" ? "Mengirim…" : EVENT.sheetEndpoint ? "Kirim" : "Kirim lewat WhatsApp"}
              {status !== "sending" && <ArrowRight className="h-4 w-4" />}
            </button>
            <p className="text-center text-xs text-slate-500">
              Jika ada kendala, <a className="font-semibold text-brand-700 hover:underline" target="_blank" rel="noopener noreferrer"
                href={`https://wa.me/${EVENT.waNumber}?text=${encodeURIComponent(`Halo, saya exhibitor di ${EVENT.name} dan tertarik untuk mencoba Landhep!`)}`}>hubungi kami</a>.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default function FleiPage() {
  const [trialOpen, setTrialOpen] = useState(false);
  const openTrial = () => setTrialOpen(true);
  // Stable, so the dialog's effect (focus + Escape) runs on open, not on every render.
  const closeTrial = useCallback(() => setTrialOpen(false), []);

  // The app shell locks the document to the viewport; this page scrolls.
  useEffect(() => {
    const els = [document.documentElement, document.body, document.getElementById("root")];
    els.forEach((el) => { if (el) { el.style.overflow = "auto"; el.style.height = "auto"; } });
    document.title = `Benefit Exhibitor ${EVENT.name} · Landhep`;
    return () => {
      els.forEach((el) => { if (el) { el.style.overflow = ""; el.style.height = ""; } });
      document.title = "landhep";
    };
  }, []);

  return (
    <div className="min-h-screen bg-brand-50 text-[#171C1F] antialiased">
      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <nav className="fixed top-0 z-50 w-full border-b border-slate-100 bg-white/90 shadow-sm backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-[1200px] items-center justify-between px-6 py-3.5 md:px-10">
          <a href="/" className="flex items-center gap-2 text-xl font-extrabold tracking-tight text-brand-700">
            <img src="/landhep-icon.png" alt="" className="h-7 w-7" />Landhep
          </a>
          <a href="#hubungi" className="inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-800">
            Hubungi Kami <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-brand-900 pb-16 pt-28 md:pb-24 md:pt-36">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(153,206,183,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(153,206,183,0.08)_1px,transparent_1px)] bg-[size:48px_48px]" />
        <div className="relative mx-auto grid w-full max-w-[1200px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[1.05fr_1fr]">
          <div className="space-y-7">
            <div className="inline-flex items-center gap-3 rounded-2xl bg-white px-4 py-2.5 shadow-lg">
              <span className="flex items-center gap-2 text-lg font-extrabold text-brand-700"><img src="/landhep-icon.png" alt="" className="h-7 w-7" />Landhep</span>
              <X className="h-4 w-4 text-slate-400" aria-hidden="true" />
              <img src={`${IMG}/fai-logo.png`} alt="Franchise Academy Indonesia" className="h-12 w-auto" />
            </div>
            <div className="space-y-4">
              <Eyebrow light>Benefit khusus exhibitor {EVENT.name.split(" ")[0]}</Eyebrow>
              <h1 className="text-4xl font-extrabold leading-[1.1] text-white md:text-5xl" style={{ textWrap: "balance" }}>
                Trial Analisa Potensi Market &amp; Lokasi Bisnis
              </h1>
              <p className="max-w-xl text-lg leading-relaxed text-brand-100/80">
                Bantu calon investor ekspansi franchise Anda di {EVENT.name} memahami potensi bisnis dan lokasi dengan data.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <button onClick={openTrial}
                className="inline-flex items-center gap-2 rounded-full bg-brand-500 px-7 py-3.5 font-semibold text-white shadow-lg shadow-brand-900/30 transition-colors hover:bg-brand-400">
                Klaim di sini <ArrowRight className="h-4 w-4" />
              </button>
              <span className="text-sm text-brand-100/60">* Free trial: satu analisa.</span>
            </div>
          </div>
          <DemoCard />
        </div>
      </section>

      {/* ── The questions an investor asks ──────────────────────────── */}
      <section className="py-20 md:py-24">
        <div className="mx-auto grid w-full max-w-[1200px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <Eyebrow>Yakinkan calon investor ekspansi</Eyebrow>
            <h2 className="mt-3 text-3xl font-bold md:text-4xl" style={{ textWrap: "balance" }}>Dukung keputusan investasi dengan data, bukan feeling</h2>
            <p className="mt-5 leading-relaxed text-[#3E4947]">
              Pemilihan lokasi adalah keputusan besar dalam bisnis: mulai dari sewa, renovasi, hingga operasional. Apakah Anda sudah siap menjawab pertanyaan ini?
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {QUESTIONS.map((q) => (
                <div key={q} className="rounded-2xl border border-brand-200/70 bg-white p-5 shadow-sm">
                  <p className="font-semibold leading-snug">“{q}”</p>
                </div>
              ))}
            </div>
            <p className="mt-6 font-semibold text-brand-700">Pastikan jawaban Anda didukung data, bukan sekadar feeling atau intuisi.</p>
          </div>
          <CandidateViz />
        </div>
      </section>

      {/* ── What Landhep is ─────────────────────────────────────────── */}
      <section className="bg-white py-20 md:py-24">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-10">
          <div className="mb-14 max-w-2xl space-y-3">
            <Eyebrow>Analisa data-driven</Eyebrow>
            <h2 className="text-3xl font-bold md:text-4xl" style={{ textWrap: "balance" }}>Landhep: market &amp; location intelligence</h2>
            <p className="leading-relaxed text-[#3E4947]">Landhep adalah platform market dan location intelligence yang membantu bisnis mengambil keputusan ekspansi berbasis data.</p>
          </div>
          <div className="space-y-16">
            {FEATURES.map(({ title, body, img, alt }, n) => (
              <div key={title} className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14">
                <div className={n % 2 ? "lg:order-2" : ""}>
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-700 text-base font-extrabold text-white">{n + 1}</span>
                  <h3 className="mt-4 text-2xl font-bold">{title}</h3>
                  <p className="mt-3 max-w-md leading-relaxed text-[#3E4947]">{body}</p>
                </div>
                <div className={n % 2 ? "lg:order-1" : ""}><Screen src={`${IMG}/${img}`} alt={alt} /></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ──────────────────────────────────────────────── */}
      <section className="py-20 md:py-24">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-10">
          <div className="mb-10 max-w-2xl space-y-3">
            <Eyebrow>Multi industri</Eyebrow>
            <h2 className="text-3xl font-bold md:text-4xl">Dibuat untuk berbagai industri</h2>
            <p className="leading-relaxed text-[#3E4947]">Analisa lokasi Landhep bisa disesuaikan dengan karakteristik tiap kategori bisnis, dari F&amp;B dan retail hingga kesehatan, hospitality, dan industri lainnya.</p>
          </div>
          <Industries />
        </div>
      </section>

      {/* ── Signals ─────────────────────────────────────────────────── */}
      <section className="bg-brand-900 py-20 text-white md:py-24">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-10">
          <div className="max-w-2xl space-y-3">
            <Eyebrow light>Lebih dari sekadar laporan</Eyebrow>
            <h2 className="text-3xl font-bold md:text-4xl" style={{ textWrap: "balance" }}>Berbagai sinyal &amp; data dalam satu laporan</h2>
            <p className="leading-relaxed text-brand-100/80">Landhep menghasilkan analisis yang akurat dan terukur dengan memanfaatkan berbagai sumber data dan dukungan AI:</p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SIGNALS.map((s, n) => {
              const Icon = SIGNAL_ICONS[n];
              return (
                <div key={s} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-500/20 text-brand-300"><Icon className="h-5 w-5" /></span>
                  <span className="font-semibold">{s}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-10 grid gap-6 border-t border-white/10 pt-8 sm:grid-cols-3">
            {PROOF.map(([big, small], n) => {
              const Icon = PROOF_ICONS[n];
              return (
                <div key={small} className="flex items-start gap-3">
                  <Icon className="mt-1 h-5 w-5 shrink-0 text-brand-300" />
                  <div><p className="text-2xl font-extrabold">{big}</p><p className="text-sm text-brand-100/70">{small}</p></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Testimonial ─────────────────────────────────────────────── */}
      <section className="py-20 md:py-24">
        <div className="mx-auto w-full max-w-[1000px] px-6 md:px-10">
          <Eyebrow>Testimoni</Eyebrow>
          <h2 className="mt-3 text-2xl font-bold md:text-3xl" style={{ textWrap: "balance" }}>{TESTIMONIAL.intro}</h2>
          <figure className="mt-8 grid items-center gap-8 rounded-3xl border border-brand-200/70 bg-white p-8 shadow-sm md:grid-cols-[180px_1fr] md:p-10">
            <img src={`${IMG}/pak-gembus-logo.png`} alt="Logo Pak Gembus" className="mx-auto h-36 w-36 object-contain" loading="lazy" />
            <div>
              <blockquote className="text-lg leading-relaxed text-[#171C1F] md:text-xl">“{TESTIMONIAL.quote}”</blockquote>
              <figcaption className="mt-4 text-sm font-semibold text-brand-700">{TESTIMONIAL.by}</figcaption>
            </div>
          </figure>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────── */}
      <section className="pb-20 md:pb-24">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-10">
          <div className="rounded-3xl bg-brand-700 px-8 py-12 text-white md:px-14 md:py-16">
            <div className="grid items-center gap-8 lg:grid-cols-[1.3fr_1fr]">
              <div>
                <Eyebrow light>Free trial khusus exhibitor {EVENT.name}</Eyebrow>
                <h2 className="mt-3 text-3xl font-extrabold md:text-4xl" style={{ textWrap: "balance" }}>Dapatkan analisa market calon lokasi ekspansi</h2>
                <p className="mt-4 max-w-xl leading-relaxed text-brand-100/80">Isi formulir untuk mendapatkan laporan yang bisa digunakan untuk diskusi dengan calon investor.</p>
              </div>
              <button onClick={openTrial}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-4 font-semibold text-brand-800 transition-colors hover:bg-brand-50">
                Klaim analisis 1 lokasi gratis <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer id="hubungi" className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 px-6 py-10 md:flex-row md:items-center md:justify-between md:px-10">
          <div>
            <p className="flex items-center gap-2 text-lg font-extrabold text-brand-700"><img src="/landhep-icon.png" alt="" className="h-7 w-7" />Landhep</p>
            <p className="mt-1 text-sm text-slate-500">Market and location intelligence untuk keputusan ekspansi.</p>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-slate-600">
            <a href={`https://wa.me/${EVENT.waNumber}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-brand-700"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
            <a href={`mailto:${EVENT.mail}`} className="inline-flex items-center gap-2 hover:text-brand-700"><Mail className="h-4 w-4" /> {EVENT.mail}</a>
            <a href="/" className="inline-flex items-center gap-2 hover:text-brand-700"><Globe className="h-4 w-4" /> landhep.id</a>
            <a href="/privasi" className="hover:text-brand-700">Kebijakan Privasi</a>
          </div>
        </div>
      </footer>

      <TrialDialog open={trialOpen} onClose={closeTrial} />
    </div>
  );
}
